// Utility functions

export function formatCurrency(value: number, currency = 'USD', locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatNumber(value: number, decimals = 0): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

export function formatDate(dateString: string, options: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
}): string {
  return new Date(dateString).toLocaleDateString('en-US', options);
}

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function getStatusColor(status: string): 'success' | 'warning' | 'danger' | 'info' | 'neutral' {
  const normalized = status.toLowerCase();
  if (['connected', 'running', 'available', 'healthy', 'active', 'open'].includes(normalized)) return 'success';
  if (['pending', 'warning', 'degraded', 'acknowledged'].includes(normalized)) return 'warning';
  if (['error', 'stopped', 'terminated', 'failed', 'unhealthy', 'resolved'].includes(normalized)) return 'danger';
  if (['info', 'unknown'].includes(normalized)) return 'info';
  return 'neutral';
}

export function getStatusBadgeClass(status: string): string {
  const color = getStatusColor(status);
  return `badge-${color}`;
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length - 1) + '…';
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

type ClassValue = string | boolean | undefined | null | Record<string, boolean | undefined | null>;

export function classNames(...classes: ClassValue[]): string {
  const out: string[] = [];
  for (const entry of classes) {
    if (!entry) continue;
    if (typeof entry === 'string') {
      out.push(entry);
    } else {
      for (const [key, value] of Object.entries(entry)) {
        if (value) out.push(key);
      }
    }
  }
  return out.join(' ');
}

export function debounce<Args extends unknown[]>(
  fn: (...args: Args) => void,
  delay: number
): (...args: Args) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

export function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce((acc, item) => {
    const groupKey = String(item[key]);
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}

export function sumBy<T>(array: T[], key: keyof T): number {
  return array.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);
}